import { motion } from "framer-motion";
import { X, RotateCcw } from "lucide-react";

/**
 * Gradient header that sits at the top of the open AskPurple chat panel.
 * Shows the mascot, the dynamic status line, a restart conversation button, and a close button.
 */
export default function ChatHeader({
  loading,
  mascotState,
  onboarded,
  profileName,
  mascotSrc,
  onClose,
  onReset,
}) {
  const status = loading
    ? "Thinking..."
    : mascotState === "typing"
      ? "Listening..."
      : mascotState === "reply"
        ? "Here's what I found!"
        : onboarded
          ? "Answers & guidance"
          : "Let's get you set up in 30 seconds";

  return (
    <div className="relative flex items-center gap-3 overflow-hidden border-b border-purple-200/40 bg-gradient-to-r from-[#5b1f6e] via-[#7C3AED] to-[#EC4899] px-4 py-3 text-white">
      <div className="pointer-events-none absolute inset-0 opacity-30">
        <div className="absolute left-[15%] top-2 h-1 w-1 rounded-full bg-pink-300" />
        <div className="absolute right-[20%] top-4 h-1.5 w-1.5 rounded-full bg-purple-300" />
        <div className="absolute left-[40%] bottom-2 h-1 w-1 rounded-full bg-amber-200" />
      </div>

      <motion.div
        animate={{ x: loading ? 0 : [0, 6, 0, -6, 0] }}
        transition={{ duration: loading ? 0.5 : 6, repeat: Infinity, ease: "easeInOut" }}
        className="relative z-10"
      >
        <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#FBE6FF] via-[#FFE0F0] to-[#FFE9D5] ring-2 ring-white/40">
          <img
            src={mascotSrc}
            alt="AskPurple"
            draggable={false}
            className="h-full w-full select-none object-cover"
          />
        </div>
      </motion.div>

      <div className="relative z-10 flex-1">
        <p className="text-sm font-semibold">AskPurple</p>
        <p className="text-xs text-white/70">{status}</p>
      </div>
      {onboarded && profileName && (
        <span className="relative z-10 rounded-full bg-white/10 px-3 py-1 text-xs">
          Hi, {profileName}
        </span>
      )}
      <div className="relative z-10 ml-1 flex items-center gap-1.5">
        {onboarded && onReset && (
          <button
            onClick={onReset}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/90 hover:bg-white/20 transition-colors cursor-pointer"
            title="Restart conversation & preferences"
            data-testid="askpurple-reset-button"
            aria-label="Restart chat"
          >
            <RotateCcw size={14} />
          </button>
        )}
        <button
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/90 hover:bg-white/20 transition-colors cursor-pointer"
          title="Close chat"
          data-testid="askpurple-close-button"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
